import React, { useEffect, useState } from 'react';
import { GenGet } from 'state/api';
import LoadingState from '../LoadingState';
import ErrorState from '../ErrorState';

function GenericDataGrid({ 
  title,
  endpoint,
  fields,
  actions,
  selectable,
  per_page = 10,
  onSelect, 
  filters = []
}) {
  const [data, setData] = useState([]); // Estado inicial é um array vazio
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);

  // Fetch data from API
  const fetchData = async () => {
    try {
      const response = await GenGet(endpoint);
      console.log(response)
      if (Array.isArray(response)) { 
        setData(response);
      } else {
        throw new Error("A resposta da API não é um array.");
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [endpoint]);

  // Filter logic
  const filteredData = data.filter(item =>
    (filters.length > 0 ? fields.filter(field => filters.includes(field.name)) : fields)
      .some(field =>
        String(item[field.name]).toLowerCase().includes(searchTerm.toLowerCase())
      )
  );

  // Pagination logic
  const indexOfLast = currentPage * per_page;
  const indexOfFirst = indexOfLast - per_page;
  const currentItems = filteredData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredData.length / per_page);

  // Selection handling
  const handleSelect = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Action buttons
  const renderActions = (row) => (
    <div className="flex gap-2">
      {actions?.map((action, index) => (
        <button
          key={index}
          onClick={() => action.handler(row)}
          className={`px-2 py-1 text-xs rounded ${action.className || 'bg-blue-500 text-white'}`}
        >
          {action.label}
        </button>
      ))}
    </div>
  );

  if (loading) return <LoadingState/>;
  if (error) return <ErrorState />;

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-3 bg-gray-100 border-b">
        <h3 className="text-lg font-semibold">{title}</h3>
        <div className="mt-2 flex justify-between items-center">
          <input
            type="text"
            placeholder="Pesquisar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-1 border rounded w-64"
          />
          <span className="text-sm text-gray-600">
            {filteredData.length} registros encontrados
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {selectable && <th className="px-4 py-2 w-12"></th>}
              {fields.map((field) => (
                <th key={field.name} className="px-4 py-2 text-left text-sm font-medium">
                  {field.label}
                </th>
              ))}
              {actions && <th className="px-4 py-2 text-sm font-medium">Ações</th>}
            </tr>
          </thead>
          
          <tbody>
            {currentItems.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50 border-t">
                {selectable && (
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(item.id)}
                      onChange={() => handleSelect(item.id)}
                    />
                  </td>
                )}
                
                {fields.map((field) => (
                  <td key={field.name} className="px-4 py-2 text-sm">
                    {field.render ? field.render(item) : item[field.name]}
                  </td>
                ))}
                
                {actions && (
                  <td className="px-4 py-2">
                    {renderActions(item)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-3 border-t bg-gray-50">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">
            Página {currentPage} de {totalPages}
          </span>
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded ${
                  currentPage === i + 1
                    ? 'bg-blue-500 text-white'
                    : 'bg-white border text-gray-700'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GenericDataGrid;